import { filter } from 'lodash';
import dataModel from '@/common/data-model';
import EssentialAppLearnMoreDialog from '@/components/app-bundle-learn-more-dialog.vue';
import AICompanionLearnMoreDialog from './ai-companion-learn-more-dialog.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import { pageConfig } from '@/common/service/page-config';
import { mapGetters } from 'vuex';
import VisitorManagementLearnMoreDialog
  from "@/components/full-comparison-table/visitor-management-learn-more-dialog.vue";
import TasksLearnMoreDialog from '@/components/full-comparison-table/tasks-learn-more-dialog.vue';
import CustomAvatarLearnMoreDialog from '@/components/full-comparison-table/custom-avatar-learn-more-dialog.vue';
import { isEnableEssentialApps } from '@/utils/toggle'
import PlanFeaturesMixin from '@/utils/planFeaturesMixin';
import { bizAndBizPlusLicenseInterval, BADGE_ID_MAPPER, CMR_STORAGE_FOR_MEETING_PLAN, BASE_PLAN_SUB_TYPES } from '@/common/constants';

const ComparisonTypeMap = {
  MEETING: 'meeting',
  PHONE: 'phone',
  PHONE_ADMIN: 'phoneAdmin',
};

const zobasicp_cmr_storage = pageConfig.zobasicp_cmr_storage || 80;
const zobasicp_meeting_minutes = pageConfig.zobasicp_meeting_minutes || 1;
const freeZoomClipsQuantity = pageConfig.freeZoomClipsQuantity;

export const ZoomMeetingComparisonDataMixins = {
  mixins: [PlanFeaturesMixin],
  data() {
    return {
      tableData: {
        meeting: [],
        phone: [],
        phoneAdmin: [],
      },
      essentialAppLearnMoreDialogService: new ServiceConfirm(EssentialAppLearnMoreDialog),
      aiCompanionLearnMoreDialogService: new ServiceConfirm(AICompanionLearnMoreDialog),
      visitorManagementLearnMoreDialogService: new ServiceConfirm(VisitorManagementLearnMoreDialog),
      tasksLearnMoreDialog: new ServiceConfirm(TasksLearnMoreDialog),
      customAvatarLearnMoreDialog: new ServiceConfirm(CustomAvatarLearnMoreDialog),
    };
  },
  created() {
    this.install();
  },
  computed: {
    ...mapGetters(['enableBizPriceIncrease']),
    treeData() {
      const mapItemKey = (data, pid) => data.map((item, idx) => ({
        ...item,
        id: `${pid}_${idx}`,
      }));
      return [{
        feature: this.$t('one.meetingFeature'),
        id: ComparisonTypeMap.MEETING,
        subTitle: true,
        children: mapItemKey(this.tableData.meeting || [], ComparisonTypeMap.MEETING),
      },
      {
        feature: this.$t('one.phoneFeature'),
        id: ComparisonTypeMap.PHONE,
        subTitle: true,
        children: mapItemKey(this.tableData.phone || [], ComparisonTypeMap.PHONE),
      },
      {
        feature: this.$t('phoneAdminFeatures'),
        id: ComparisonTypeMap.PHONE_ADMIN,
        subTitle: true,
        children: mapItemKey(this.tableData.phoneAdmin || [], ComparisonTypeMap.PHONE_ADMIN),
      }];
    },
  },
  methods: {
    install() {
      let me = this;
      const meetingFeatures = [
        {
          feature: this.$t('meetings.fullPlanItem1'),
          tooltip: this.$t('meetings.fullPlanItem1Tip'),
          isString: true,
          basic: 100,
          basicPlus: 100,
          zobasicp: 100,
          zobasicpm: 100,
          pro: 100,
          business: 300,
          businessPlus: 300,
          enterprise: this.$t('meetings.fullPlanItem1_ent')
        }, this.$context.is.edit ? null : {
          feature: this.$t('meetings.fullPlanItem2'),
          tooltip: this.$t('meetings.fullPlanItem2Tip'),
          isString: true,
          basic: '1',
          basicPlus: '1',
          zobasicp: '1',
          zobasicpm: '1',
          pro: '1-99',
          business: bizAndBizPlusLicenseInterval,
          businessPlus: bizAndBizPlusLicenseInterval,
          enterprise: '> 250'
        }, {
          feature: this.$t('meetings.fullPlanItem6'),
          tooltip: this.$t('meetings.fullPlanItem6Tip'),
          isString: true,
          basic: this.$t('meetings.fullPlanItem6_basic'),
          basicPlus: this.$t('meetings.fullPlanItem6_basic'),
          zobasicp: this.$t('meetings.fullPlanItem6_basic'),
          zobasicpm: this.$t('meetings.fullPlanItem6_basic'),
          pro: this.$t('meetings.fullPlanItem6_pro'),
          business: this.$t('meetings.fullPlanItem6_biz'),
          businessPlus: this.$t('meetings.fullPlanItem6_biz'),
          enterprise: this.$t('meetings.fullPlanItem6_ent')
        }, {
          feature: this.$t('meetings.fullPlanItem3'),
          isString: true,
          basic: this.$t('meetings.fullPlanItem3_basic'),
          basicPlus: this.$t('meetings.fullPlanItem3_basic'),
          zobasicp: this.$t('billing.cycle.monthly'),
          zobasicpm: this.$t('billing.cycle.monthly'),
          pro: this.$t('meetings.fullPlanItem3_pro'),
          business: this.$t('meetings.fullPlanItem3_biz'),
          businessPlus: this.$t('meetings.fullPlanItem3_pro'),
          enterprise: this.$t('meetings.fullPlanItem3_ent')
        }, {
          feature: this.$t('meetings.fullPlanItem4'),
          basic: true,
          basicPlus: true,
          zobasicp: false,
          zobasicpm: false,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem5'),
          tooltip: this.$t('meetings.fullPlanItem5Tip'),
          isString: true,
          basic: this.$t('meetings.fullPlanItem5_basic'),
          basicPlus: this.$t('one.basicPlusFullPlanItem1'),
          zobasicp: this.$t('marketing.new_pricing.basic_plus.comparison.meeting_duration', [zobasicp_meeting_minutes]),
          zobasicpm: this.$t('marketing.new_pricing.basic_plus.comparison.meeting_duration', [zobasicp_meeting_minutes]),
          pro: this.$t('meetings.fullPlanItem5_pro'),
          business: this.$t('meetings.fullPlanItem5_biz'),
          businessPlus: this.$t('meetings.fullPlanItem5_biz'),
          enterprise: this.$t('meetings.fullPlanItem5_ent')
        }, {
          feature: this.$t('meetings.fullPlanItem7'),
          tooltip: this.$t('meetings.fullPlanItem7Tip'),
          isString: true,
          basic: this.$t('meetings.fullPlanItem7_basic'),
          basicPlus: this.$t('meetings.fullPlanItem7_basic'),
          zobasicp: this.$t('marketing.new_pricing.basic_plus.comparison.recording', [zobasicp_cmr_storage + 'GB']),
          zobasicpm: this.$t('marketing.new_pricing.basic_plus.comparison.recording', [zobasicp_cmr_storage + 'GB']),
          pro: this.$t('meetings.fullPlanItemCmr', { capacity: CMR_STORAGE_FOR_MEETING_PLAN[BASE_PLAN_SUB_TYPES.PRO] }),
          business: this.$t('meetings.fullPlanItemCmr', { capacity: CMR_STORAGE_FOR_MEETING_PLAN[BASE_PLAN_SUB_TYPES.BIZ] }),
          businessPlus: this.$t('meetings.fullPlanItemCmr', { capacity: CMR_STORAGE_FOR_MEETING_PLAN[BASE_PLAN_SUB_TYPES.ZOOM_ONE_BIZ_PLUS] }),
          enterprise: this.$t('meetings.fullPlanItem7_ent')
        }, {
          feature: this.$t('meetings.fullPlanItem9'),
          tooltip: this.$t('meetings.fullPlanItem9Tip'),
          isString: true,
          pro: this.$t('meetings.fullPlanItem9_pro'),
          business: this.$t('meetings.fullPlanItem9_biz'),
          businessPlus: this.$t('meetings.fullPlanItem9_biz'),
          enterprise: this.$t('meetings.fullPlanItem9_ent')
        }, {
          shouldHide: !isEnableEssentialApps(),
          feature: this.$t('billing.essential_apps'),
          link: {
            text: this.$t('common.learnMore'),
            style: {
              fontSize: '12px',
              fontWeight: 700,
              color: '#0B5CFF'
            },
            ariaLabel: 'Learn more about Essential Apps',
            async callback () {
              await me.essentialAppLearnMoreDialogService.confirm();
            }
          },
          isString: true,
          basic: false,
          basicPlus: false,
          pro: this.$t('meetings.fullPlanItem_EssentialAppText'),
          business: this.$t('meetings.fullPlanItem_EssentialAppText'),
          businessPlus: this.$t('meetings.fullPlanItem_EssentialAppText'),
          enterprise: false,
        }, {
          feature: this.$t('meetings.fullPlanItem11'),
          tooltip: this.$t('meetings.fullPlanItem11Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem12'),
          tooltip: this.$t('meetings.fullPlanItem12Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem14'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem15'),
          tooltip: this.$t('meetings.fullPlanItem15Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('one.messaging'),
          tooltip: this.$t('meetings.fullPlanItem16Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem17'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem18'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem19'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem21'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('endToEndEncryptionForZoomMeetings'),
          tooltip: this.$t('whenEnabledZoomsEndToEndEncryptionE2eeForZoomMeetings'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('enterpriseGradeSecurityFeatures'),
          tooltip: this.$t('256BitAesGcmEncryptionForRealTime'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem29'),
          tooltip: this.$t('meetings.fullPlanItem29Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem41'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem61'),
          tooltip: this.$t('meetings.fullPlanItem61Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem62'),
          tooltip: this.$t('meetings.fullPlanItem62Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanNewItem1'),
          tooltip: this.$t('meetings.fullPlanNewItem1Tip'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanNewItem2'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanNewItem3'),
          tooltip: this.$t('meetings.fullPlanNewItem3Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem24'),
          tooltip: this.$t('meetings.fullPlanItem24Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem25'),
          tooltip: this.$t('meetings.fullPlanItem25Tip'),
          isString: true,
          pro: this.$t('meetings.fullPlanItem25_pro'),
          business: this.$t('meetings.fullPlanItem25_pro'),
          businessPlus: this.$t('meetings.fullPlanItem25_biz'),
          enterprise: this.$t('meetings.fullPlanItem25_biz')
        }, {
          feature: this.$t('meetings.fullPlanItem26'),
          tooltip: this.$t('meetings.fullPlanItem26Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem27'),
          tooltip: this.$t('meetings.fullPlanItem27Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem28'),
          tooltip: this.$t('meetings.fullPlanItem28Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem30'),
          tooltip: this.$t('meetings.fullPlanItem30Tip'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('one.autoCaptions'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true,
          desc: {
            basic: this.$t('billing.pbx.multi_language'),
            basicPlus: this.$t('billing.pbx.multi_language'),
            zobasicp: this.$t('billing.pbx.multi_language'),
            zobasicpm: this.$t('billing.pbx.multi_language'),
            pro: this.$t('billing.pbx.multi_language'),
            business: this.$t('billing.pbx.multi_language'),
            businessPlus: this.$t('billing.pbx.multi_language'),
            enterprise: this.$t('billing.pbx.multi_language'),
          },
        }, {
          feature: this.$t('meetings.fullPlanItem31'),
          tooltip: this.$t('meetings.fullPlanItem31Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem32'),
          tooltip: this.$t('meetings.fullPlanItem32Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem33'),
          tooltip: this.$t('meetings.fullPlanItem33Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem34'),
          tooltip: this.$t('meetings.fullPlanItem34Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem35'),
          tooltip: this.$t('meetings.fullPlanItem35Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem37'),
          tooltip: this.$t('meetings.fullPlanItem37Tip'),
          business: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem39'),
          tooltip: this.$t('meetings.fullPlanItem39Tip'),
          enterprise: true
        }, {
          feature: this.$t('meetings.fullPlanItem40')
        }, {
          feature: this.$t('meetings.fullPlanNewItem4'),
          enterprise: true,
          enterpriseDesc: this.$t('one.includeWbn1000')
        }, {
          shouldHide: dataModel.billingConf.isWhiteboardAddonDisabled,
          feature: this.$t('meetings.fullPlanItem51'),
          isString: true,
          basic: '3',
          basicPlus: '3',
          zobasicp: '3',
          zobasicpm: '3',
          pro: '3',
          business: this.$t('meetings.unlimited'),
          businessPlus: this.$t('meetings.unlimited'),
          enterprise: this.$t('meetings.unlimited')
        }, {
          feature: this.$t('one.translatedCations'),
          businessPlus: true,
          enterprise: this.$t('one.entPlus')
        },
        {
          shouldHide: !dataModel.showMailFeature,
          feature: this.$t('mail'),
          tooltip: this.$t('zoomMailAndCalendarInTheZoomApp'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          shouldHide: !dataModel.showMailFeature || !dataModel.showMailFeatureSubtitle,
          feature: this.$t('mailService'),
          tooltip: this.$t('emailServiceWithFullTimeEndToEnd'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          shouldHide: !dataModel.showMailFeature,
          feature: this.$t('customMailDomain'),
          tooltip: this.$t('customMailDomainWithMailService'),
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          shouldHide: !dataModel.showMailFeature,
          feature: this.$t('calendarClient'),
          tooltip: this.$t('scheduleAndJoinMeetings'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          shouldHide: !dataModel.showMailFeature || !dataModel.showMailFeatureSubtitle,
          feature: this.$t('calendarService'),
          tooltip: this.$t('meetingSchedulingAndAppointment'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          feature: this.$t('marketing.new_pricing.ai_companion'),
          link: {
            text: this.$t('common.learnMore'),
            style: {
              fontSize: '12px',
              fontWeight: 700,
              color: '#0B5CFF'
            },
            ariaLabel: 'Learn more about AI Companion',
            async callback () {
              await me.aiCompanionLearnMoreDialogService.confirm();
            }
          },
          basic: false,
          basicPlus: window.newBillingConfig.enableDocsFeature,
          zobasicp: window.newBillingConfig.enableDocsFeature,
          zobasicpm: window.newBillingConfig.enableDocsFeature,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        window.newBillingConfig.disabledClips ? null : {
          feature: this.$t('marketing.new_pricing.clips'),
          tooltip: this.$t('marketing.new_pricing.clips_tips'),
          isString: true,
          basic: this.$t('marketing.new_pricing.included_x', [freeZoomClipsQuantity]),
          basicPlus: this.$t('marketing.new_pricing.included_x', [freeZoomClipsQuantity]),
          zobasicp: this.$t('marketing.new_pricing.included_x', [freeZoomClipsQuantity]),
          zobasicpm: this.$t('marketing.new_pricing.included_x', [freeZoomClipsQuantity]),
          pro: this.$t('meetings.unlimited'),
          business: this.$t('meetings.unlimited'),
          businessPlus: this.$t('meetings.unlimited'),
          enterprise: this.$t('meetings.unlimited')
        },
        {
          shouldHide: !window.newBillingConfig.isEligibleAccount4CustomAvatarFeature,
          feature: this.$t('billing.custom_avatars.title'),
          badgeId: BADGE_ID_MAPPER.CUSTOM_AVATAR,
          tooltip: this.$t('billing.custom_avatars.comparison_table_tips'),
          isString: true,
          basic: false,
          basicPlus: false,
          zobasicp: false,
          zobasicpm: false,
          pro: this.$t('billing.custom_avatars.feature'),
          business: this.$t('billing.custom_avatars.feature'),
          businessPlus: this.$t('billing.custom_avatars.feature'),
          enterprise: this.$t('billing.custom_avatars.feature'),
        },
        {
          feature: this.$t('marketing.new_pricing.notes'),
          tooltip: this.$t('marketing.new_pricing.notes_tips'),
          basic: true,
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        !window.newBillingConfig.enableDocsFeature ? null : {
          feature: this.$t('billing.docs.docs'),
          tooltip: this.$t('billing.docs.comparison_table_tips'),
          basic: this.$t('billing.limited_features'),
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true
        },
        {
          shouldHide: !window.newBillingConfig.enableDisplayZoomTasksWithoutAccountCheck,
          feature: this.$t('billing.tasks.tasks'),
          badgeId: BADGE_ID_MAPPER.TASKS,
          tooltip: this.$t('billing.tasks.comparison_table_tips'),
          basic: this.$t('billing.limited_features'),
          basicPlus: true,
          zobasicp: true,
          zobasicpm: true,
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          shouldHide: !window.newBillingConfig.enableDisplayZoomDrive,
          mixed: true,
          feature: this.$t('billing.plan_feature.drive.title'),
          tooltip: this.$t('billing.plan_feature.drive.tip'),
          basic: this.$t('billing.limited_features'),
          basicPlus: this.$t('billing.limited_features'),
          zobasicp: this.$t('billing.limited_features'),
          zobasicpm: this.$t('billing.limited_features'),
          pro: true,
          business: true,
          businessPlus: true,
          enterprise: true,
          badgeId: BADGE_ID_MAPPER.DRIVE
        },
        {
          shouldHide: !window.newBillingConfig.enableDisplayZoomWorkFlow,
          feature: this.$t('billing.plan_feature.workflow.title'),
          badgeId: BADGE_ID_MAPPER.WORKFLOW_AUTOMATION,
          tooltip: this.$t('billing.plan_feature.workflow.tip'),
          mixed: true,
          basic: false,
          basicPlus: this.$t('billing.plan_feature.workflow.label'),
          zobasicp: this.$t('billing.plan_feature.workflow.label'),
          zobasicpm: this.$t('billing.plan_feature.workflow.label'),
          pro: this.$t('billing.plan_feature.workflow.label'),
          business: this.$t('billing.plan_feature.workflow.label'),
          businessPlus: this.$t('billing.plan_feature.workflow.label'),
          enterprise: this.$t('billing.plan_feature.workflow.label'),
          link: {
            text: this.$t('common.learnMore'),
            style: {
              fontSize: '12px',
              fontWeight: 700,
              color: '#0B5CFF'
            },
            ariaLabel: 'Learn more about Workflow Automation',
            async callback () {
              await me.openDialogInCommon(me.featureKeys.zoomWorkFlowAutomation);
            }
          },
        },
        {
          shouldHide: !dataModel.showCustomerManagedKeyFeature,
          feature: this.$t('zoomCustomerManagedKey'),
          tooltip: this.$t('bringYourOwnEncryptionKeysToProtectZoomDataAtRest'),
          enterprise: true
        },
        {
          feature: this.$t('billing.scheduler.short_title'),
          tooltip: this.$t('billing.scheduler.feature_tooltip'),
          pro: false,
          business: !!this.enableBizPriceIncrease,
          businessPlus: true
        },
      ].filter(x => x);
      const phoneFeatures = [
        {
          feature: this.$t('phone.fullPlanItem40'),
          tooltip: this.$t('phone.fullPlanItem40Tip'),
          proPlus: false,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem1'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem2'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem3'),
          proPlus: true,
          businessPlus: true
        }, {
          feature: this.$t('phone.fullPlanItem4'),
          tooltip: this.$t('phone.fullPlanItem4Tip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem5'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem6'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem7'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem8'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem9'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem10'),
          tooltip: this.$t('phone.fullPlanItem10Tip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem11'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem12'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem13'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem14'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem15'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem16'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem17'),
          tooltip: this.$t('phone.fullPlanItem17Tip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem18'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem19'),
          tooltip: this.$t('phone.fullPlanItem19Tip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem20'),
          tooltip: this.$t('phone.fullPlanItem20Tip'),
          pdf: true,
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem21'),
          tooltip: this.$t('phone.fullPlanItem21Tip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem22'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem23'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem24'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('endToEndEncryptionForZoomPhone'),
          tooltip: this.$t('whenEnabledForZoomPhoneEndToEndEncryptionE2ee'),
          basic: true,
          basicPlus: true,
          pro: true,
          proPlus: true,
          business: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem43'),
          proPlus: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem44'),
          proPlus: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem46'),
          proPlus: false,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem47'),
          tooltip: this.$t('phone.fullPlanItem47Tip'),
          proPlus: false,
          businessPlus: true,
          enterprise: true,
        },
        {
          isString: true,
          feature: this.$t('billing.pbx.phone_support_24_7_live_chat'),
          tooltip: this.$t('availableToAllPhoneStandaloneCustomersSupportPlansWillVaryWhenPairedWithMeetingsPlans'),
          proPlus: this.$t('billing.zone_pro_plus.pro_plus_phone_features'),
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem49'),
          tooltip: this.$t('phone.fullPlanItem49Tip'),
          proPlus: false,
          businessPlus: true,
          enterprise: true,
        },
        {
          feature: this.$t('phone.fullPlanItem50'),
          proPlus: false,
          businessPlus: true,
          enterprise: true,
        },
        {
          suppurtCountry: 'us',
          feature: this.$t('phone.fullPlanSms'),
          tooltip: this.$t('phone.fullPlanSmsTip'),
          proPlus: true,
          businessPlus: true,
          enterprise: true
        },
        {
          feature: this.$t('phone.fullPlanItem51'),
          proPlus: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          shouldHide: !['US', 'CA'].includes(this.currentCountry),
          feature: this.$t('phone.fullPlanItem61'),
          tooltip: this.$t('phone.fullPlanItem61Tip', { href: '/billing/pbx/rates' }),
          pro: false,
          proPlus: true,
          businessPlus: true,
        }
      ];
      const phoneAdminFeatures = [
        {
          feature: this.$t('phone.fullPlanItem43'),
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem44'),
          businessPlus: true,
          enterprise: true
        },
        {
          feature: this.$t('phone.fullPlanItem46'),
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem47'),
          tooltip: this.$t('phone.fullPlanItem47Tip'),
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('billing.pbx.phone_support_24_7_live_chat'),
          tooltip: this.$t('billing.pbx.phone_support_24_7_live_chat_tip'),
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem49'),
          tooltip: this.$t('phone.fullPlanItem49Tip'),
          businessPlus: true,
          enterprise: true
        }, {
          feature: this.$t('phone.fullPlanItem50'),
          businessPlus: true,
          enterprise: true
        }, {
          suppurtCountry: 'us',
          feature: this.$t('phone.fullPlanSms'),
          tooltip: this.$t('phone.fullPlanSmsTip'),
          businessPlus: true,
          enterprise: true
        },
        {
          feature: this.$t('phone.fullPlanItem51'),
          proPlus: true,
          businessPlus: true,
          enterprise: true,
        },
        {
          shouldHide: !['US', 'CA'].includes(this.currentCountry),
          feature: this.$t('phone.fullPlanItem61'),
          tooltip: this.$t('phone.fullPlanItem61Tip', { href: '/billing/pbx/rates' }),
          pro: false,
          proPlus: true,
          businessPlus: true,
        }
      ];
      this.meetingFeatures = filter(meetingFeatures, (item) => { return item.shouldHide !== true; });
      this.phoneFeatures = filter(phoneFeatures, (item) => { return item.shouldHide !== true; });
      this.phoneAdminFeatures = filter(phoneAdminFeatures, (item) => { return item.shouldHide !== true; });
      this.tableData.meeting = this.meetingFeatures;
      this.tableData.phone = this.phoneFeatures;
      this.tableData.phoneAdmin = this.phoneAdminFeatures;
      this.updateTableData(this.currentCountry);
    },
    updateTableData (country) {
      if (!country) { return; }
      if (country.toLocaleLowerCase() === 'us' || country.toLocaleLowerCase() === 'ca') {
        this.tableData.meeting = this.meetingFeatures;
        this.tableData.phone = this.phoneFeatures;
        this.tableData.phoneAdmin = this.phoneAdminFeatures;
      } else {
        this.tableData.meeting = filter(this.tableData.meeting, (item) => { return item.suppurtCountry !== 'us'; });
        this.tableData.phone = filter(this.tableData.phone, (item) => { return item.suppurtCountry !== 'us'; });
        this.tableData.phoneAdmin = filter(this.tableData.phoneAdmin, (item) => { return item.suppurtCountry !== 'us'; });
      }
    },
  }
}
